import { useAppTheme } from "@hooks/useAppTheme";
import type { SituationCard } from "@memorang/types/src/session/new-session";
import { Box, MarkdownRenderer } from "@memorang/ui";
import { useCallback, useEffect, useRef, useState } from "react";
import { Pressable, ScrollView, View, useWindowDimensions } from "react-native";
import { Caption, Icon, Text } from "react-native-paper";
import Animated, {
	interpolate,
	useAnimatedStyle,
	useSharedValue,
	withTiming,
} from "react-native-reanimated";

interface Props {
	situations: SituationCard[];
	situationIndex?: number;
	situationTotal?: number;
	onAllViewed?: () => void;
}

const CARD_HEIGHT = 260;

const OutcomeFlipCard = ({
	label,
	content,
	reviewed,
	onViewed,
}: {
	label: string;
	content: string;
	reviewed: boolean;
	onViewed: () => void;
}) => {
	const theme = useAppTheme();
	const flipped = useSharedValue(0);
	const [isFlipped, setIsFlipped] = useState(false);

	const frontStyle = useAnimatedStyle(() => ({
		transform: [
			{ perspective: 1000 },
			{ rotateY: `${interpolate(flipped.value, [0, 1], [0, 180])}deg` },
		],
		opacity: flipped.value < 0.5 ? 1 : 0,
	}));

	const backStyle = useAnimatedStyle(() => ({
		transform: [
			{ perspective: 1000 },
			{ rotateY: `${interpolate(flipped.value, [0, 1], [180, 360])}deg` },
		],
		opacity: flipped.value >= 0.5 ? 1 : 0,
	}));

	const handleFlip = () => {
		const next = flipped.value === 0 ? 1 : 0;
		flipped.value = withTiming(next, { duration: 350 });
		setIsFlipped(next === 1);
		if (next === 1) {
			onViewed();
		}
	};

	const baseFaceStyle = {
		position: "absolute" as const,
		width: "100%" as const,
		height: "100%" as const,
		backfaceVisibility: "hidden" as const,
		borderWidth: 1,
		overflow: "hidden" as const,
	};

	const Header = ({ revealed }: { revealed: boolean }) => (
		<Box
			flexDirection="row"
			alignItems="center"
			justifyContent="space-between"
			gap={10}
			padding={12}
			borderBottomWidth={1}
			borderBottomColor={theme.colors.outlineVariant}
		>
			<Box flexDirection="row" alignItems="center" gap={10} flex={1}>
				<Box
					width={32}
					height={32}
					alignItems="center"
					justifyContent="center"
					backgroundColor={theme.colors.primary}
				>
					<Icon
						source={revealed ? "eye-outline" : "eye-off-outline"}
						size={16}
						color={theme.colors.onPrimary}
					/>
				</Box>
				<Text
					variant="titleMedium"
					style={{ color: theme.colors.onSurface, fontWeight: "800" }}
				>
					{label}
				</Text>
			</Box>
			{reviewed ? null : (
				<Box
					width={8}
					height={8}
					borderRadius={4}
					backgroundColor={theme.colors.error}
				/>
			)}
			<Icon
				source="rotate-3d-variant"
				size={18}
				color={theme.colors.onSurfaceVariant}
			/>
		</Box>
	);

	return (
		<View style={{ flex: 1, minWidth: 0, height: CARD_HEIGHT }}>
			<Animated.View
				style={[
					baseFaceStyle,
					{
						borderColor: theme.colors.outlineVariant,
						backgroundColor: theme.colors.surface,
					},
					frontStyle,
				]}
				accessibilityElementsHidden={isFlipped}
				importantForAccessibility={isFlipped ? "no-hide-descendants" : "auto"}
			>
				<Pressable
					onPress={handleFlip}
					style={{ flex: 1 }}
					accessibilityRole="button"
					accessibilityLabel={label}
					accessibilityHint="Double tap to flip"
				>
					<Header revealed={false} />
					<Box
						flex={1}
						justifyContent="center"
						alignItems="center"
						gap={10}
						padding={16}
					>
						<Box
							width={48}
							height={48}
							alignItems="center"
							justifyContent="center"
							backgroundColor={theme.colors.primaryContainer}
						>
							<Icon
								source="gesture-tap"
								size={24}
								color={theme.colors.primary}
							/>
						</Box>
						<Text
							variant="bodyMedium"
							style={{
								color: theme.colors.onSurfaceVariant,
								textAlign: "center",
								lineHeight: 21,
							}}
						>
							Select to reveal this possible outcome.
						</Text>
					</Box>
				</Pressable>
			</Animated.View>

			<Animated.View
				style={[
					baseFaceStyle,
					{
						borderColor: theme.colors.primary,
						backgroundColor: theme.colors.primaryContainer,
						pointerEvents: isFlipped ? "auto" : "none",
					},
					backStyle,
				]}
				accessibilityElementsHidden={!isFlipped}
				importantForAccessibility={isFlipped ? "auto" : "no-hide-descendants"}
			>
				<Pressable
					onPress={handleFlip}
					accessibilityRole="button"
					accessibilityLabel={`${label} outcome`}
					accessibilityHint="Double tap to flip back"
				>
					<Header revealed />
				</Pressable>
				<ScrollView
					style={{ flex: 1 }}
					contentContainerStyle={{ padding: 16 }}
					showsVerticalScrollIndicator={false}
				>
					<MarkdownRenderer
						text={content}
						variant="explanation"
						fontSize={15}
						customStyles={{
							paragraph: { fontSize: 15, lineHeight: 22.5 },
							text: { fontSize: 15, lineHeight: 22.5 },
						}}
					/>
				</ScrollView>
			</Animated.View>
		</View>
	);
};

const EthicalSituationCards = ({
	situations,
	situationIndex,
	situationTotal,
	onAllViewed,
}: Props) => {
	const theme = useAppTheme();
	const { width } = useWindowDimensions();
	const isStacked = width < 900;
	const situation = situations[0];
	const outcomes = situation?.back.outcomes ?? [];
	const [viewedOutcomes, setViewedOutcomes] = useState(() => new Set<number>());
	const hasMarkedAllViewed = useRef(false);

	const markOutcomeViewed = useCallback((index: number) => {
		setViewedOutcomes((current) => {
			if (current.has(index)) {
				return current;
			}

			const next = new Set(current);
			next.add(index);
			return next;
		});
	}, []);

	useEffect(() => {
		if (
			outcomes.length &&
			viewedOutcomes.size === outcomes.length &&
			!hasMarkedAllViewed.current
		) {
			hasMarkedAllViewed.current = true;
			onAllViewed?.();
		}
	}, [onAllViewed, outcomes.length, viewedOutcomes.size]);

	if (!situation) {
		return null;
	}

	const showCounter =
		situationIndex != null && situationTotal != null && situationTotal > 1;

	return (
		<Box width="100%" gap={18}>
			<Box
				width="100%"
				borderWidth={1}
				borderColor={theme.colors.outlineVariant}
				backgroundColor={theme.colors.surface}
			>
				<Box padding={18} gap={12}>
					{showCounter ? (
						<Caption
							style={{
								color: theme.colors.primary,
								fontWeight: "800",
								letterSpacing: 0.8,
								textTransform: "uppercase",
							}}
						>
							{situationIndex! + 1} of {situationTotal}
						</Caption>
					) : null}
					<Text
						variant="titleMedium"
						style={{
							color: theme.colors.onSurface,
							fontWeight: "800",
							fontSize: 18,
						}}
					>
						{situation.front.title}
					</Text>
					<Text
						variant="bodyLarge"
						style={{ color: theme.colors.onSurface, lineHeight: 24 }}
					>
						{situation.front.text}
					</Text>
				</Box>
			</Box>

			<Box width="100%" flexDirection={isStacked ? "column" : "row"} gap={16}>
				{outcomes.map((outcome, index) => (
					<OutcomeFlipCard
						key={`${outcome.label}-${index}`}
						label={outcome.label}
						content={outcome.content}
						reviewed={viewedOutcomes.has(index)}
						onViewed={() => markOutcomeViewed(index)}
					/>
				))}
			</Box>
		</Box>
	);
};

export default EthicalSituationCards;
